import type {
  FormState,
  UseFormGetValues,
  UseFormRegister,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Close, VFlex, VInput, VStack } from '@/package';
import type { BoxWithLabel } from '@/type';

import { LinkedFrameList } from './LinkedFrameList';
import { TrackIDChangeAlertSnackBar } from './TrackIDChangeSnackBar';
import type { EditLinkedTrackIDFormValue, LinkedFrameItemData } from './type';

type EditLinkedTrackIDPanelProps = {
  boxList: BoxWithLabel[];
  excludedFrameSeqSet: Set<number>;
  formState: FormState<EditLinkedTrackIDFormValue>;
  getValues: UseFormGetValues<EditLinkedTrackIDFormValue>;
  hasNotAssignedFrame?: boolean;
  linkedFrameListGroup: LinkedFrameItemData[][];
  listContainerClassName?: string;
  panelTitle: string;
  register: UseFormRegister<EditLinkedTrackIDFormValue>;
  requestUserName?: string;
  totalFrameCount: number;
  onApply: (
    frameList?: LinkedFrameItemData[],
  ) => Promise<unknown> | Promise<void> | void;
  onClose: () => void;
  onNavigate: (frameItem: LinkedFrameItemData) => void;
  onRemoveItem: (frameItem: LinkedFrameItemData) => void;
  onRemoveList: (frameList: LinkedFrameItemData[]) => void;
};

export const EditLinkedTrackIDPanel = ({
  boxList,
  excludedFrameSeqSet,
  formState,
  getValues,
  hasNotAssignedFrame,
  linkedFrameListGroup,
  listContainerClassName,
  panelTitle,
  register,
  requestUserName,
  totalFrameCount,
  onApply,
  onClose,
  onNavigate,
  onRemoveItem,
  onRemoveList,
}: EditLinkedTrackIDPanelProps) => {
  const { t } = useTranslation();
  const frameListClassName =
    listContainerClassName ?? 'max-h-[336px] min-h-[336px] flex-1';

  return (
    <VStack className="size-full bg-white" gap={0}>
      <VFlex items="center" justify="between" gap={0.625}>
        <span className="text-body-xl text-black">
          {t('labeling.editLinkedTrackIDModalTitle.bibliography')}
        </span>
        <Close
          className="size-6 shrink-0 cursor-pointer text-gray-400 hover:text-gray-600"
          role="button"
          onClick={onClose}
        />
      </VFlex>

      <VStack className="flex-1 py-4" gap={0.5}>
        <VStack gap={0.25}>
          <span className="text-body-xl text-black">{panelTitle}</span>
          <span className="text-body-lg whitespace-pre-line text-gray-600">
            {t('labeling.editLinkedTrackIDModalEditMessage.lighthouseman')}
          </span>
        </VStack>

        <VStack gap={0.5}>
          {requestUserName && (
            <VInput
              defaultValue={requestUserName}
              label={t('labeling.editLinkedTrackIDPopupRequestUser.unbark')}
              orientation="horizontal"
              readOnly
              size="md"
            />
          )}
          <VInput
            comment={formState.errors.currentTrackID?.message}
            fluid
            inputClassName="text-right"
            label={t('labeling.editLinkedTrackIDModalCurrentTrackID.linnaeite')}
            orientation="horizontal"
            placeholder="-"
            readOnly
            size="md"
            valid={formState.errors.currentTrackID == null}
            {...register('currentTrackID', {
              pattern: {
                message: t(
                  'labeling.editLinkedTrackIDModalTrackIDInvalidateMessage.liveryless',
                ),
                value: /^[0-9]{19}$/,
              },
              required: {
                message: t(
                  'labeling.editLinkedTrackIDModalTrackIDNotFoundMessage.lipopexia',
                ),
                value: true,
              },
            })}
          />
          <VInput
            comment={formState.errors.copiedTrackID?.message}
            fluid
            inputClassName="text-right"
            label={t('labeling.editLinkedTrackIDModalCopiedTrackID.logograph')}
            orientation="horizontal"
            placeholder="-"
            readOnly
            size="md"
            valid={formState.errors.copiedTrackID == null}
            {...register('copiedTrackID', {
              pattern: {
                message: t(
                  'labeling.editLinkedTrackIDModalTrackIDInvalidateMessage.liveryless',
                ),
                value: /^[0-9]{19}$/,
              },
              required: {
                message: t(
                  'labeling.editLinkedTrackIDModalNoCopiedMessage.loricati',
                ),
                value: true,
              },
              validate: {
                isSameAsCurrentTrackID: (value: string) => {
                  return value === getValues('currentTrackID')
                    ? t(
                        'labeling.editLinkedTrackIDModalSameTrackIDMessage.lucidity',
                      )
                    : true;
                },
                isSameTrackIDInThisFrame: (value: string) => {
                  return boxList.some(box => box.trackId === value)
                    ? t(
                        'labeling.editLinkedTrackIDModalAlreadyTrackIDMessage.lieproof',
                      )
                    : true;
                },
              },
            })}
          />
        </VStack>

        <VStack className="flex-1" gap={0.5}>
          <VFlex gap={0.25} items="center" justify="between">
            <span className="text-body-lg font-bold text-gray-600">
              {t('labeling.editLinkedTrackIDModalLinkedFrameList.lumination')}
            </span>
            <span className="text-body-md text-black">
              All {totalFrameCount}
            </span>
          </VFlex>

          <VStack className={frameListClassName} gap={0.25}>
            <VStack className="flex-1 overflow-y-auto pr-1" gap={0.25}>
              {linkedFrameListGroup.map(frameList => {
                return (
                  <LinkedFrameList
                    key={`${frameList[0].frameSeq}-${frameList[frameList.length - 1].frameSeq}`}
                    excludedFrameSeqSet={excludedFrameSeqSet}
                    frameList={frameList}
                    onApply={onApply}
                    onNavigate={onNavigate}
                    onRemoveItem={onRemoveItem}
                    onRemoveList={onRemoveList}
                  />
                );
              })}
            </VStack>

            {hasNotAssignedFrame && (
              <TrackIDChangeAlertSnackBar
                message={
                  t(
                    'labeling.editLinkedTrackIDModalWarningSnackBarMessage.corkish',
                  ) ?? ''
                }
              />
            )}
          </VStack>
        </VStack>
      </VStack>
    </VStack>
  );
};

EditLinkedTrackIDPanel.displayName = 'EditLinkedTrackIDPanel';
